import React from "react";
import PulseDot from "./PulseDot";
import AnimatedMetric from "./AnimatedMetric";
import DemoBadge from "./DemoBadge";
import { Droplets, Gauge, Thermometer, Wind, BatteryCharging } from "lucide-react";

const stateClass = (s) =>
  s === "good" || s === "online" ? "text-[color:var(--auditiq-success)]" :
  s === "warning" || s === "degraded" ? "text-[color:var(--auditiq-warning)]" :
  "text-[color:var(--auditiq-critical)]";

export const WaterProductionPanel = ({ data }) => {
  if (!data) return null;
  return (
    <div className="glass p-0 overflow-hidden" data-testid="water-prod-panel">
      <div className="flex items-center justify-between p-5 border-b border-gold-dim/20">
        <div>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-electric">
            <PulseDot tone="electric" /> Water Production Engine
          </div>
          <h3 className="font-display text-xl mt-1">{data.facility}</h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Efficiency vs target</div>
          <div className="metric text-3xl text-gold">
            <AnimatedMetric value={data.efficiency_pct} decimals={1} suffix="%" testid="water-eff" />
          </div>
          <DemoBadge />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5 border-b border-gold-dim/15">
        <StatTile icon={<Droplets size={18} />} label="Current L/hour" value={data.current_total_lph} decimals={1} />
        <StatTile icon={<Gauge size={18} />}    label="Projected L/day" value={data.projected_total_lpd} decimals={0} />
        <StatTile icon={<Wind size={18} />}     label="Target L/day"    value={data.target_facility_lpd} decimals={0} hint="6 × 10 000 L" />
        <StatTile icon={<BatteryCharging size={18} />} label="Units Online" value={data.unit_count} decimals={0} />
      </div>

      <div className="data-line" />

      <div className="p-5 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.units.map(u => (
          <div key={u.unit_id} className="panel p-4" data-testid={`awg-${u.unit_id}`}>
            <div className="flex items-center justify-between">
              <div className="font-mono text-gold">{u.unit_id}</div>
              <PulseDot tone={u.pump_status === "online" ? "electric" : "critical"} />
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-steel">Output (L/h) · Daily est.</div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="metric text-2xl text-cream">
                <AnimatedMetric value={u.water_output_lph} decimals={1} />
              </div>
              <div className="text-xs text-cream-dim">→ <AnimatedMetric value={u.water_output_daily_est_l} decimals={0} suffix=" L/day" /></div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <Tiny label="Humidity"   value={`${u.humidity_pct}%`} icon={<Droplets size={12} />} />
              <Tiny label="Temp"       value={`${u.temperature_c}°C`} icon={<Thermometer size={12} />} />
              <Tiny label="Airflow"    value={`${u.airflow_cfm} CFM`} />
              <Tiny label="Energy"     value={`${u.energy_kw} kW`} />
              <Tiny label="Filter"     value={u.filter_condition} className={stateClass(u.filter_condition)} />
              <Tiny label="Pump"       value={u.pump_status} className={stateClass(u.pump_status)} />
              <Tiny label="Storage"    value={`${u.storage_level_pct}%`} />
              <Tiny label="Fail prob"  value={`${u.failure_probability_pct}%`}
                    className={u.failure_probability_pct > 15 ? "text-[color:var(--auditiq-critical)]" : ""} />
            </div>
            {u.alerts?.length > 0 && (
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[color:var(--auditiq-critical)]">
                ⚠ {u.alerts.join(" · ")}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const StatTile = ({ icon, label, value, decimals, hint }) => (
  <div className="panel p-3">
    <div className="flex items-center gap-2 text-steel">{icon}<span className="text-[10px] uppercase tracking-[0.22em]">{label}</span></div>
    <div className="metric text-2xl text-cream mt-1">
      <AnimatedMetric value={value} decimals={decimals} />
    </div>
    {hint && <div className="text-[10px] text-steel mt-1">{hint}</div>}
  </div>
);

const Tiny = ({ label, value, icon, className = "" }) => (
  <div className="flex items-center justify-between bg-navy-deep/60 rounded px-2 py-1">
    <span className="flex items-center gap-1 text-steel">{icon}{label}</span>
    <span className={`font-mono ${className || "text-cream-dim"}`}>{value}</span>
  </div>
);

export default WaterProductionPanel;
